import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { ApiTokens } from "../components/ApiTokens";
import { useAuth } from "../lib/auth";
import type { StaffAccount } from "../lib/types";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  PageHeader,
  Select,
  Table,
  TableWrap,
  Td,
  Th,
  formatDate,
} from "../components/ui";

interface Issued {
  email: string;
  password: string;
  /** A reset reads differently from a new account, and says so. */
  reason: "created" | "reset";
}

/**
 * What each level actually means, said where the choice is made rather than in
 * a document nobody opens. An operator is the one worth spelling out: it looks
 * like an admin everywhere in the interface, so the difference has to be stated
 * here or it will be picked by accident.
 */
const LEVEL_HINTS: Record<string, string> = {
  operator:
    "Runs the business — orders, projects, customers, the catalogue, the blog. Cannot see, issue or revoke licences, and cannot reach the signing keys.",
  owner:
    "Everything an admin does, plus licences, signing keys, the audit trail, and who can sign in — including removing you.",
  admin: "Runs the business. No access to licences, signing keys or the audit trail.",
};

/**
 * What each level is called on screen.
 *
 * The stored values are `operator`, `admin` and `owner`; these are the names
 * the business uses. An operator is simply "the admin", because that is what
 * the person holding it is — the licence custody an ordinary admin would also
 * have is the exception here, not the rule, and it belongs to the one account
 * that owns the platform.
 */
/**
 * Whether this level can reach licences, signing keys and the audit trail.
 * Only the owner can. Asked as a positive so a level added later has to be
 * granted custody deliberately rather than inheriting it.
 */
function hasLicensing(role: string): boolean {
  return role === "owner";
}

const LEVEL_LABELS: Record<string, string> = {
  operator: "Admin",
  // Stored `admin` is a legacy tier with no custody of its own any more, so it
  // reads as what it now is. Nothing creates one; existing accounts keep working.
  admin: "Admin",
  owner: "Super admin",
};

export function Accounts() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [adding, setAdding] = useState(false);
  const [issued, setIssued] = useState<Issued | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);

  const accounts = useQuery({
    queryKey: ["accounts"],
    queryFn: () => api<{ items: StaffAccount[] }>("/api/v1/admin/accounts"),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ["accounts"] });

  const create = useMutation({
    mutationFn: (body: { email: string; role: string }) =>
      api<{ account: StaffAccount; password: string }>("/api/v1/admin/accounts", {
        method: "POST",
        body,
      }),
    onSuccess: (result) => {
      setAdding(false);
      setIssued({ email: result.account.email, password: result.password, reason: "created" });
      invalidate();
    },
  });

  const setRole = useMutation({
    mutationFn: (input: { id: string; role: string }) =>
      api(`/api/v1/admin/accounts/${input.id}/role`, { method: "PATCH", body: { role: input.role } }),
    onSuccess: invalidate,
  });

  const setDisabled = useMutation({
    mutationFn: (input: { id: string; disabled: boolean }) =>
      api(`/api/v1/admin/accounts/${input.id}/disabled`, {
        method: "PATCH",
        body: { disabled: input.disabled },
      }),
    onSuccess: invalidate,
  });

  const resetPassword = useMutation({
    mutationFn: (account: StaffAccount) =>
      api<{ password: string }>(`/api/v1/admin/accounts/${account.id}/reset-password`, {
        method: "POST",
        body: {},
      }).then((result) => ({ ...result, email: account.email })),
    onSuccess: (result) => {
      setIssued({ email: result.email, password: result.password, reason: "reset" });
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/v1/admin/accounts/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      setConfirming(null);
      invalidate();
    },
  });

  const failed = [create, setRole, setDisabled, resetPassword, remove].find((m) => m.isError);
  const busy = [setRole, setDisabled, resetPassword, remove].some((m) => m.isPending);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Staff accounts"
        lede="Who can sign in to this panel, and at what level. Only an owner sees this screen."
        action={
          <Button variant="primary" onClick={() => setAdding((value) => !value)}>
            {adding ? "Cancel" : "Add account"}
          </Button>
        }
      />

      {issued !== null && (
        <Card title={issued.reason === "created" ? "Account created" : "Password reset"}>
          <div className="space-y-4 p-4">
            <p className="text-sm text-muted">
              Send this to {issued.email}. It is shown once — only its hash is stored, so there is
              no way to read it back. Ask them to turn on two-factor from Settings once they are in.
            </p>
            <code className="block break-all rounded border border-accent/40 bg-base px-3 py-3 tabular text-base text-ink">
              {issued.password}
            </code>
            <div className="flex gap-2">
              <Button
                variant="primary"
                onClick={() => void navigator.clipboard.writeText(issued.password)}
              >
                Copy password
              </Button>
              <Button onClick={() => setIssued(null)}>I have sent it</Button>
            </div>
          </div>
        </Card>
      )}

      {failed !== undefined && <ErrorNote>{(failed.error as Error).message}</ErrorNote>}

      <Card>
        {adding && (
          <AccountForm
            pending={create.isPending}
            onSubmit={(body) => create.mutate(body)}
          />
        )}

        {accounts.isPending ? (
          <EmptyState>Loading…</EmptyState>
        ) : (
          <TableWrap>
            <Table>
              <thead className="border-b border-hairline">
                <tr>
                  <Th>Email</Th>
                  <Th>Level</Th>
                  <Th>Two-factor</Th>
                  <Th>Last signed in</Th>
                  <Th>Status</Th>
                  <Th className="text-right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {(accounts.data?.items ?? []).map((account) => {
                  // Every destructive control is hidden on your own row rather
                  // than shown and refused. The server refuses it too — this is
                  // so nobody has to discover that by clicking.
                  const isSelf = account.id === user?.id;

                  return (
                    <tr key={account.id} className="border-b border-hairline last:border-0">
                      <Td label="Email">
                        {account.email}
                        {isSelf && <span className="ml-2 text-xs text-dim">you</span>}
                      </Td>
                      <Td label="Level">
                        <Badge tone={account.role === "owner" ? "accent" : "quiet"}>
                          {LEVEL_LABELS[account.role] ?? account.role}
                        </Badge>
                      </Td>
                      <Td label="Two-factor">
                        {account.totpEnrolled ? (
                          <span className="text-xs text-muted">On</span>
                        ) : (
                          <span className="text-xs text-dim">Off</span>
                        )}
                      </Td>
                      <Td label="Last signed in" className="text-muted">
                        {account.lastLoginAt === null ? (
                          <span className="text-dim">Never</span>
                        ) : (
                          formatDate(account.lastLoginAt)
                        )}
                      </Td>
                      <Td label="Status">
                        <Badge tone={account.disabled ? "danger" : "live"}>
                          {account.disabled ? "Disabled" : "Active"}
                        </Badge>
                      </Td>
                      <Td label="Actions" className="text-right">
                        {isSelf ? (
                          <span className="text-xs text-dim">
                            Ask another owner to change your own account
                          </span>
                        ) : confirming === account.id ? (
                          <div className="flex flex-wrap justify-end gap-2">
                            <span className="self-center text-xs text-muted">
                              Remove {account.email}?
                            </span>
                            <Button
                              variant="danger"
                              disabled={remove.isPending}
                              onClick={() => remove.mutate(account.id)}
                            >
                              Remove
                            </Button>
                            <Button onClick={() => setConfirming(null)}>Keep</Button>
                          </div>
                        ) : (
                          <div className="flex flex-wrap justify-end gap-2">
                            <Button
                              disabled={busy}
                              onClick={() =>
                                setRole.mutate({
                                  id: account.id,
                                  role: hasLicensing(account.role) ? "operator" : "owner",
                                })
                              }
                            >
                              {hasLicensing(account.role) ? "Make admin" : "Make super admin"}
                            </Button>
                            {/* Disabling is the reversible one, and is almost
                                always what is actually wanted. */}
                            <Button
                              disabled={busy}
                              onClick={() =>
                                setDisabled.mutate({ id: account.id, disabled: !account.disabled })
                              }
                            >
                              {account.disabled ? "Enable" : "Disable"}
                            </Button>
                            <Button disabled={busy} onClick={() => resetPassword.mutate(account)}>
                              Reset password
                            </Button>
                            <Button variant="ghost" onClick={() => setConfirming(account.id)}>
                              Remove
                            </Button>
                          </div>
                        )}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>

      <p className="text-xs text-dim">
        Disabling ends any session that account is holding, straight away. Removing is permanent,
        but what the account did stays in the audit trail.
      </p>

      {/* Machine access lives beside human access: both are answers to "who
          can reach this platform", and both are the owner's to grant. */}
      <ApiTokens />
    </div>
  );
}

function AccountForm({
  pending,
  onSubmit,
}: {
  pending: boolean;
  onSubmit: (body: { email: string; role: string }) => void;
}) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("operator");

  return (
    <form
      className="space-y-4 border-b border-hairline p-4"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit({ email: email.trim().toLowerCase(), role });
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Email">
          <Input
            type="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>
        <Field
          label="Level"
          hint={LEVEL_HINTS[role] ?? LEVEL_HINTS["admin"]!}
        >
          <Select value={role} onChange={(event) => setRole(event.target.value)}>
            <option value="operator">Admin</option>
            <option value="owner">Super admin</option>
          </Select>
        </Field>
      </div>

      <div className="flex gap-2">
        <Button type="submit" variant="primary" disabled={pending || email.trim() === ""}>
          {pending ? "Creating…" : "Create account"}
        </Button>
      </div>

      <p className="text-xs text-dim">
        A password is generated and shown once. There is no email being sent yet, so you pass it on
        yourself.
      </p>
    </form>
  );
}
